import parsePasswordValidationMap from '../_helpers/passwordValidation';
import {
  GraphPointStyleIcon,
  GraphPointStyles,
  Option,
  ParametersType,
  PriorityList,
  ThresholdConfig,
  UserConfig
} from './telosair-interfaces';

export const CO2 = 'CO₂';
export const COMFORT_FACTOR = 'CI';
export const AQI = 'AQI';
export const TEMP = 'TEMP';
export const RH = 'RH';
export const PRES = 'PRES';
export const PN03 = 'PN03';
export const PM25 = 'PM2.5';
export const PM10 = 'PM10';
export const PN25 = 'PN2.5'; // TODO: This 2.5 should be subscript
export const VOC = 'VOCs';
export const HUM = 'HUM';

export const AUTO_REFRESH_TIME = 20000;
export const DEFAULT_START_TIME = 15;
export const GRAPH_COLORS = {
  level: '#98A2BC',
  grid: '#1D2939'
};
export const DROPDOWN_CONTROL_CHILD_CLASS = 'material-icons-outlined text-gray-200';
export const DROPDOWN_CONTROL_CHILD_CLASS_ACTIVE =
  'material-icons-outlined text-gray-300 group-hover:text-gray-700';
export const DROPDOWN_CONTROL_CLASS =
  'inline-flex w-fit justify-center items-center gap-x-1.5 rounded border border-gray-200 border-gray-700 shadow-xs bg-container-500 px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-gray-300  hover:border-primary-500';
export const DROPDOWN_CONTROL_CLASS_ACTIVE =
  'inline-flex w-fit justify-center items-center gap-x-1.5 rounded border border-gray-200 border-gray-700 shadow-xs bg-container-500 px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-gray-300 hover:border-primary-500';

export const ParameterTooltips: Record<ParametersType, string> = {
  pm25_m: 'Mass of particulate matter smaller than 2.5 µm',
  pm100_m: 'Mass of particulate matter smaller than 10 µm',
  pn03_m: 'Number concentration of particles larger than 0.3 µm',
  co2: 'Concentration of CO2',
  tvoc: 'Concentration of volatile organic compounds (VOCs). Note that the sensor provides a measure of total VOCs as an index ranging in values from 0 to 500. The sensor might have different sensitivity to different compounds',
  aqi: 'Air quality index. A value below 100 is good. Using the approach of US EPA (https://www.epa.gov/outdoor-air-quality-data/how-aqi-calculated), the indoor AQI value is calculated from the measurements of PM2.5, PM10, PN0.3, VOC, and CO2 (other values can be added if additional sensor measurements are available). Typically, the annual average limit (as specified by ASHRAE/EPA/estimated) is used to scale the AQI value to 50 and the daily safe limit is used to set an AQI value of 100.',
  ci: 'Comfort Index. A value below 100 is good. Based on temperature, relative, and pressure values. Set threshold values to define a safe limit (CI <= 50) and values for acceptable limit (CI <=100).',
  hum: 'Air humidity in %',
  temp: 'Air temperature',
  pressure: 'Air pressure',
  ch4: 'Concentration of methane',
  so2: 'Concentration of sulfur dioxide',
  co: 'Concentration of carbon monoxide',
  no2: 'Concentration of nitrogen dioxide',
  o3: 'Concentration of ozone'
};

export const COLORS = [
  '#00CED1', // Dark Turquoise
  '#FFD700', // Gold
  '#32CD32', // Lime Green
  '#FF8C00', // Dark Orange
  '#8A2BE2', // Blue Violet
  '#FF69B4', // Hot Pink
  '#7FFF00', // Chartreuse
  '#40E0D0', // Turquoise
  '#D2691E', // Chocolate
  '#FF7F50', // Coral
  '#6495ED', // Cornflower Blue
  '#20B2AA', // Light Sea Green
  '#800080', // Purple
  '#FFA500', // Orange
  '#48D1CC', // Medium Turquoise
  '#ADFF2F', // Green Yellow
  '#87CEEB', // Sky Blue
  '#DC143C', // Crimson
  '#4682B4', // Steel Blue
  '#00FF7F', // Spring Green
  '#FF4500', // Orange Red
  '#DA70D6', // Orchid
  '#9ACD32', // Yellow Green
  '#00FA9A', // Medium Spring Green
  '#B0E0E6', // Powder Blue
  '#FF6347', // Tomato
  '#00BFFF', // Deep Sky Blue
  '#7B68EE', // Medium Slate Blue
  '#FFDAB9', // Peach Puff
  '#F08080', // Light Coral
  '#66CDAA', // Medium Aquamarine
  '#8B0000', // Dark Red
  '#E9967A', // Dark Salmon
  '#8FBC8F', // Dark Sea Green
  '#483D8B', // Dark Slate Blue
  '#FFD700', // Gold
  '#FF1493', // Deep Pink
  '#98FB98', // Pale Green
  '#FFB6C1', // Light Pink
  '#4682B4', // Steel Blue
  '#FFA07A', // Light Salmon
  '#00FF00', // Lime
  '#7CFC00', // Lawn Green
  '#8B4513', // Saddle Brown
  '#00FFFF', // Aqua
  '#B22222', // Firebrick
  '#FF00FF', // Magenta
  '#BDB76B', // Dark Khaki
  '#FFFACD', // Lemon Chiffon
  '#FF6347', // Tomato
  '#D8BFD8' // Thistle
];

export const POINT_STYLES: GraphPointStyles[] = [
  'rect',
  'cross',
  'triangle',
  'rectRot',
  'crossRot',
  'star',
  'circle',
  'rectRounded'
];

export const COLOR_BY_PRIORITY = {
  [PriorityList.critical]: '#C14237',
  [PriorityList.high]: '#C14237',
  [PriorityList.medium]: '#C1851C',
  [PriorityList.low]: '#32A74C',
  [PriorityList.none]: '#344054',
  [PriorityList.outOfRange]: '#344054'
};

export const alertStatus = {
  dismiss: 'Dismissed',
  resolved: 'Resolved',
  pending: 'Pending',
  all: 'all'
};

export const POINT_STYLE_ICON: GraphPointStyleIcon = {
  circle: 'circle',
  rect: 'square',
  rectRounded: 'square',
  rectRot: 'rectangle',
  cross: 'rectangle',
  crossRot: 'rectangle',
  star: 'rectangle',
  triangle: 'triangle'
};

export const DEFAULT_UNITS: { [key in ParametersType]: string } = {
  ci: '',
  aqi: '',
  pressure: '"H2O',
  pn03_m: '#/cm3',
  temp: '℃',
  hum: '%',
  co2: 'ppm',
  tvoc: 'ppb',
  pm25_m: 'ug/m^3',
  pm100_m: 'ug/m^3',
  ch4: 'ppm',
  so2: 'ppb',
  co: 'ppm',
  no2: 'ppb',
  o3: 'ppm'
};

export const ALL_PARAMETERS: { [key in ParametersType]: any } = {
  ci: {
    minKey: 'ciMin' as keyof UserConfig,
    maxKey: 'ciMax' as keyof UserConfig,
    thresholdKey: 'ciThreshold' as keyof UserConfig,
    icon: './assets/voc.dark.svg',
    color: '#E55381',
    unitKey: ''
  },
  aqi: {
    minKey: 'aqiMin' as keyof UserConfig,
    maxKey: 'aqiMax' as keyof UserConfig,
    thresholdKey: 'aqiThreshold' as keyof UserConfig,
    icon: './assets/voc.dark.svg',
    color: '#7B3E19',
    unitKey: ''
  },
  temp: {
    minKey: 'tempMin',
    maxKey: 'tempMax',
    thresholdKey: 'tempThreshold',
    icon: './assets/temperature.dark.svg',
    color: '#4825DC',
    unitKey: 'temperature'
  },
  hum: {
    minKey: 'humidityMin',
    maxKey: 'humidityMax',
    thresholdKey: 'reativeHumidityThreshold',
    icon: './assets/rh.dark.svg',
    color: '#B9D34C',
    unit: '',
    unitKey: 'relativeHumidity'
  },
  pressure: {
    minKey: 'pressureMin',
    maxKey: 'pressureMax',
    thresholdKey: 'pressureThreshold',
    icon: './assets/humidity.dark.svg',
    color: '#AF17E4',
    unitKey: 'pressure'
  },
  co2: {
    minKey: 'co2Min',
    maxKey: 'co2Max',
    thresholdKey: 'co2Threshold',
    icon: './assets/co2.dark.svg',
    color: '#8525FF',
    unitKey: 'co2'
  },
  pm25_m: {
    minKey: 'pm25Min' as keyof UserConfig,
    maxKey: 'pm25Max' as keyof UserConfig,
    thresholdKey: 'pm25Threshold',
    icon: './assets/particulate.dark.svg',
    color: '#528BFF',
    unitKey: 'pmType'
  },
  // pn25_m: {
  //   minKey: 'pn25Min' as keyof UserConfig,
  //   maxKey: 'pn25Max' as keyof UserConfig,
  //   thresholdKey: 'pn25Threshold' as keyof UserConfig,
  //   icon: './assets/particulate.dark.svg',
  //   color: '#E98B2F',
  //   parameterName: 'pn25_m',
  //   displayName: 'PN2.5',
  //   label: PN25,
  //   unit: '',
  //   unitKey: 'pnType'
  // },
  pm100_m: {
    minKey: 'pm100Min' as keyof UserConfig,
    maxKey: 'pm100Max' as keyof UserConfig,
    thresholdKey: 'pm100Threshold' as keyof UserConfig,
    icon: './assets/particulate.dark.svg',
    color: '#10b981',
    unitKey: 'pmType'
  },
  pn03_m: {
    minKey: 'pn03Min' as keyof UserConfig,
    maxKey: 'pn03Max' as keyof UserConfig,
    thresholdKey: 'pn03Threshold' as keyof UserConfig,
    icon: './assets/particulate.dark.svg',
    color: '#facc15',
    unitKey: 'pnType'
  },
  tvoc: {
    minKey: 'tvocMin',
    maxKey: 'tvocMax',
    thresholdKey: 'vocsThreshold',
    icon: './assets/voc.dark.svg',
    color: '#fb923c',
    unitKey: 'tvoc'
  },
  ch4: {
    minKey: 'ch4Min',
    maxKey: 'ch4Max',
    thresholdKey: 'ch4Threshold',
    icon: './assets/voc.dark.svg',
    color: '#fb923c',
    unitKey: 'ch4'
  },
  so2: {
    minKey: 'so2Min',
    maxKey: 'so2Max',
    thresholdKey: 'so2Threshold',
    icon: './assets/voc.dark.svg',
    color: '#fb923c',
    unitKey: 'so2'
  },
  co: {
    minKey: 'coMin',
    maxKey: 'coMax',
    thresholdKey: 'coThreshold',
    icon: './assets/voc.dark.svg',
    color: '#fb923c',
    unitKey: 'co'
  },
  no2: {
    minKey: 'no2Min',
    maxKey: 'no2Max',
    thresholdKey: 'no2Threshold',
    icon: './assets/voc.dark.svg',
    color: '#fb923c',
    unitKey: 'no2'
  },
  o3: {
    minKey: 'o3Min',
    maxKey: 'o3Max',
    thresholdKey: 'o3Threshold',
    icon: './assets/voc.dark.svg',
    color: '#fb923c',
    unitKey: 'o3'
  }
};
export const ROLE_BY_ID = {
  ROLE_USER: 'Operator',
  ROLE_COMPANY_ADMIN: 'Owner',
  ROLE_SYSTEM_ADMIN: 'Administrator',
  ROLE_SYS_ADMIN: 'Administrator',
  ROLE_ADMIN: 'Administrator',
  Operator: 'Operator',
  Administrator: 'Administrator'
};
export const ROLES = [
  {
    value: 'ROLE_COMPANY_ADMIN',
    label: 'Owner'
  },
  {
    value: 'ROLE_SYSTEM_ADMIN',
    label: 'Administrator'
  },
  {
    value: 'ROLE_USER',
    label: 'Operator'
  }
];

export const Headers = [];

export const IconSourceDescription = [
  {
    id: 'capture',
    icon: 'photo_camera',
    title: 'Capture a Screen',
    description: 'Use this feature to create an image of the current dashboard.',
    class: 'right-0 transition-all duration-100 hidden z-10',
    visibleIn: ['dashboard', 'data']
  },
  {
    id: 'download',
    icon: 'download',
    title: 'Save a Document',
    description: 'Use this feature to download the current data in CSV format.',
    class: 'right-0 transition-all duration-100 hidden z-10',
    visibleIn: ['dashboard']
  },
  {
    id: 'upload',
    icon: 'upload',
    title: 'Upload a Document',
    description: 'Use this feature to upload a document to the current data in CSV format.',
    class: 'right-0 transition-all duration-100 hidden z-10',
    visibleIn: ['dashboard']
  }
];

export const VIEW_FLOOR = 'floor-view';
export const VIEW_TABLE = 'table-view';

export const VIEW_BUTTONS = [
  { id: VIEW_TABLE, name: 'Table View', icon: 'table_view' },
  { id: VIEW_FLOOR, name: 'Floor View', icon: 'grid_view' }
];

export const DAHBOARD_DEFAULT_FILTER = 'All Devices';
export const OVERVIEW_GROUP = { name: DAHBOARD_DEFAULT_FILTER } as Option<number>;

export const INDUSTRIES = [
  'Education',
  'Healthcare',
  'Hospitality',
  'Manufacturing',
  'Mining',
  'Data Centers',
  'Office Buildings',
  'Construction',
  'Entertainment',
  'Agriculture',
  'Pharmaceuticals',
  'Automotive',
  'Energy',
  'Transportation and Logistics',
  'Environmental Monitoring',
  'Retail',
  'Smart Cities',
  'Marine and Offshore',
  'Aerospace',
  'Consumer Electronics',
  'Others'
];

// const PARAMETERS = [
//   { parameter_name: COMFORT_FACTOR, parameter_id: 1 },
//   { parameter_name: AQI, parameter_id: 2 },
//   { parameter_name: TEMP, parameter_id: 3 },
//   { parameter_name: RH, parameter_id: 4 },
//   { parameter_name: PRES, parameter_id: 5 },
//   { parameter_name: CO2, parameter_id: 6 },
//   { parameter_name: PM25, parameter_id: 7 },
//   { parameter_name: VOC, parameter_id: 8 }
// ];
// eslint-disable-next-line no-shadow
export enum TOAST_MESSAGES {
  error = 'An unknown error occured, please try again later.',
  created = 'Created successfully',
  added = 'Added successfully',
  edited = 'Edited successfully',
  deleted = 'Deleted successfully'
}

// eslint-disable-next-line no-useless-escape
// export const EMAIL_VALIDATOR_PATTERN = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
export const EMAIL_VALIDATOR_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const PASSWORD_VALIDATOR_PATTERN =
  /^(?=.*[0-9])(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*[a-z])(?!.*\s).{10,}$/;
export const LINK_VALIDATOR_PATTERN =
  // eslint-disable-next-line no-useless-escape
  /^(https?:\/\/)?((([a-zA-Z\d]([a-zA-Z\d-]*[a-zA-Z\d])*)\.)+[a-zA-Z]{2,}|\d{1,3}(\.\d{1,3}){3}|\[([a-fA-F\d:]+)\])(:\d+)?(\/[-a-zA-Z\d%_.~+]*)*(\?[;&a-zA-Z\d%_.~+=-]*)?(\#[-a-zA-Z\d_]*)?$/;

export const RHFPatterns = {
  email: {
    value: EMAIL_VALIDATOR_PATTERN,
    message: 'Please enter a valid email address'
  },
  password: {
    value: PASSWORD_VALIDATOR_PATTERN,
    message: `Password should contain at least 1 uppercase, 1 lowercase, 1 digit,
    1 special character, have a length of at least of 10 and no whitespace`
  },
  url: {
    value: LINK_VALIDATOR_PATTERN,
    message: 'Please enter a valid URL'
  }
};

export const RHFValidations = {
  emailRequired: {
    required: 'Please enter the email address',
    pattern: RHFPatterns.email
  },
  emailNotRequired: {
    pattern: RHFPatterns.email
  },
  password: parsePasswordValidationMap('password'),
  otp: {
    required: 'Verification code is required',
    maxLength: {
      value: 6,
      message: 'Enter a valid 6 digit verification code'
    },
    minLength: {
      value: 6,
      message: 'Enter a valid 6 digit verification code'
    }
  }
};

export const PRIORITY_LIST = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

export const priorityList = [
  { value: PRIORITY_LIST.critical, label: PRIORITY_LIST.critical },
  { value: PRIORITY_LIST.high, label: PRIORITY_LIST.high },
  { value: PRIORITY_LIST.medium, label: PRIORITY_LIST.medium },
  { value: PRIORITY_LIST.low, label: PRIORITY_LIST.low }
];

export const VALIDATION_APIS = [
  '/device/',
  '/groups/',
  '/alerts/delete/',
  '/alerts/',
  '/device/add-device',
  '/device',
  '/user/save',
  '/gateway/',
  '/facility/notification',
  '/user',
  '/user/setting',
  '/public/client/update-status'
];

export const TIMEZONES_WITH_DETAIL: any = {
  'Pacific/Midway': {
    name: 'Midway Island, Samoa',
    abbr: 'SST',
    time: 'GMT-11'
  },
  'Pacific/Niue': {
    name: 'Niue',
    abbr: 'NUT',
    time: 'GMT-11'
  },
  'Pacific/Pago_Pago': {
    name: 'Pago Pago',
    abbr: 'SST',
    time: 'GMT-11'
  },
  'Pacific/Honolulu': {
    name: 'Hawaii',
    abbr: 'HST',
    time: 'GMT-10'
  },
  'Pacific/Rarotonga': {
    name: 'Rarotonga',
    abbr: 'CKT',
    time: 'GMT-10'
  },
  'Pacific/Tahiti': {
    name: 'Tahiti',
    abbr: 'TAHT',
    time: 'GMT-10'
  },
  'Pacific/Marquesas': {
    name: 'Marquesas Islands',
    abbr: 'MART',
    time: 'GMT-9:30'
  },
  'America/Adak': {
    name: 'Adak',
    abbr: 'HADT',
    time: 'GMT-9'
  },
  'America/Anchorage': {
    name: 'Anchorage',
    abbr: 'AKDT',
    time: 'GMT-8'
  },
  'America/Juneau': {
    name: 'Alaska',
    abbr: 'AKDT',
    time: 'GMT-8'
  },
  'America/Nome': {
    name: 'Nome',
    abbr: 'AKDT',
    time: 'GMT-8'
  },
  'America/Sitka': {
    name: 'Sitka',
    abbr: 'AKDT',
    time: 'GMT-8'
  },
  'America/Yakutat': {
    name: 'Yakutat',
    abbr: 'AKDT',
    time: 'GMT-8'
  },
  'America/Dawson': {
    name: 'Dawson, Yukon',
    abbr: 'PDT',
    time: 'GMT-7'
  },
  'America/Whitehorse': {
    name: 'Whitehorse',
    abbr: 'PDT',
    time: 'GMT-7'
  },
  'America/Creston': {
    name: 'Creston',
    abbr: 'MST',
    time: 'GMT-7'
  },
  'America/Dawson_Creek': {
    name: 'Dawson Creek',
    abbr: 'MST',
    time: 'GMT-7'
  },
  'America/Fort_Nelson': {
    name: 'Fort Nelson',
    abbr: 'MST',
    time: 'GMT-7'
  },
  'America/Los_Angeles': {
    name: 'Pacific Time',
    abbr: 'PDT',
    time: 'GMT-7'
  },
  'America/Tijuana': {
    name: 'Tijuana',
    abbr: 'PDT',
    time: 'GMT-7'
  },
  'America/Vancouver': {
    name: 'Vancouver',
    abbr: 'PDT',
    time: 'GMT-7'
  },
  'America/Phoenix': {
    name: 'Arizona',
    abbr: 'MST',
    time: 'GMT-7'
  },
  'America/Boise': {
    name: 'Mountain Time',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Cambridge_Bay': {
    name: 'Cambridge Bay',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Denver': {
    name: 'Denver',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Edmonton': {
    name: 'Edmonton',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Hermosillo': {
    name: 'Hermosillo',
    abbr: 'MST',
    time: 'GMT-7'
  },
  'America/Inuvik': {
    name: 'Inuvik',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Mazatlan': {
    name: 'Mazatlan',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Ojinaga': {
    name: 'Ojinaga',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Yellowknife': {
    name: 'Yellowknife',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Chihuahua': {
    name: 'Chihuahua, La Paz, Mazatlan',
    abbr: 'MDT',
    time: 'GMT-6'
  },
  'America/Belize': {
    name: 'Central America',
    abbr: 'CST',
    time: 'GMT-6'
  },
  'America/Chicago': {
    name: 'Central Time',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Matamoros': {
    name: 'Matamoros',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Regina': {
    name: 'Saskatchewan',
    abbr: 'CST',
    time: 'GMT-6'
  },
  'America/Mexico_City': {
    name: 'Guadalajara, Mexico City, Monterrey',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Bahia_Banderas': {
    name: 'Bahia Banderas',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Merida': {
    name: 'Merida',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Monterrey': {
    name: 'Monterrey',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Winnipeg': {
    name: 'Winnipeg',
    abbr: 'CDT',
    time: 'GMT-5'
  },
  'America/Detroit': {
    name: 'Eastern Time',
    abbr: 'EDT',
    time: 'GMT-4'
  },
  'America/New_York': {
    name: 'New York',
    abbr: 'EDT',
    time: 'GMT-4'
  },
  'America/Bogota': {
    name: 'Bogota, Lima, Quito',
    abbr: 'COT',
    time: 'GMT-5'
  },
  'America/Caracas': {
    name: 'Caracas, La Paz',
    abbr: 'VET',
    time: 'GMT-4'
  },
  'America/Anguilla': {
    name: 'Anguilla',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Antigua': {
    name: 'Antigua',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Aruba': {
    name: 'Aruba',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Asuncion': {
    name: 'Asuncion',
    abbr: 'PYT',
    time: 'GMT-4'
  },
  'America/Barbados': {
    name: 'Barbados',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Blanc-Sablon': {
    name: 'Blanc-Sablon',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Boa_Vista': {
    name: 'Boa Vista',
    abbr: 'AMT',
    time: 'GMT-4'
  },
  'America/Campo_Grande': {
    name: 'Campo Grande',
    abbr: 'AMT',
    time: 'GMT-4'
  },
  'America/Cuiaba': {
    name: 'Cuiaba',
    abbr: 'AMT',
    time: 'GMT-4'
  },
  'America/Curacao': {
    name: 'Curacao',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Dominica': {
    name: 'Dominica',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Eirunepe': {
    name: 'Eirunepe',
    abbr: 'AMT',
    time: 'GMT-5'
  },
  'America/Glace_Bay': {
    name: 'Glace Bay',
    abbr: 'ADT',
    time: 'GMT-3'
  },
  'America/Goose_Bay': {
    name: 'Goose Bay',
    abbr: 'ADT',
    time: 'GMT-3'
  },
  'America/Grenada': {
    name: 'Grenada',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Guadeloupe': {
    name: 'Guadeloupe',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Guyana': {
    name: 'Guyana',
    abbr: 'GYT',
    time: 'GMT-4'
  },
  'America/Havana': {
    name: 'Havana',
    abbr: 'CST',
    time: 'GMT-5'
  },
  'America/La_Paz': {
    name: 'La Paz',
    abbr: 'BOT',
    time: 'GMT-4'
  },
  'America/Manaus': {
    name: 'Manaus',
    abbr: 'AMT',
    time: 'GMT-4'
  },
  'America/Marigot': {
    name: 'Marigot',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Martinique': {
    name: 'Martinique',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Montserrat': {
    name: 'Montserrat',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Nassau': {
    name: 'Nassau',
    abbr: 'EDT',
    time: 'GMT-4'
  },
  'America/Paramaribo': {
    name: 'Paramaribo',
    abbr: 'SRT',
    time: 'GMT-3'
  },
  'America/Port_of_Spain': {
    name: 'Port of Spain',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Port-au-Prince': {
    name: 'Port-au-Prince',
    abbr: 'EDT',
    time: 'GMT-4'
  },
  'America/Porto_Velho': {
    name: 'Porto Velho',
    abbr: 'AMT',
    time: 'GMT-4'
  },
  'America/Puerto_Rico': {
    name: 'Puerto Rico',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Santo_Domingo': {
    name: 'Santo Domingo',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/St_Barthelemy': {
    name: 'St Barthelemy',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/St_Kitts': {
    name: 'St Kitts',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/St_Lucia': {
    name: 'St Lucia',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/St_Thomas': {
    name: 'St Thomas',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/St_Vincent': {
    name: 'St Vincent',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Thule': {
    name: 'Thule',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Tortola': {
    name: 'Tortola',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Toronto': {
    name: 'Toronto',
    abbr: 'EDT',
    time: 'GMT-4'
  },
  'America/Lower_Princes': {
    name: 'Lower Princes',
    abbr: 'AST',
    time: 'GMT-4'
  },
  'America/Santiago': {
    name: 'Santiago',
    abbr: 'CLT',
    time: 'GMT-3'
  },
  'America/St_Johns': {
    name: 'Newfoundland and Labrador',
    abbr: 'NDT',
    time: 'GMT-2:30'
  },
  'America/Sao_Paulo': {
    name: 'Brasilia',
    abbr: 'BRT',
    time: 'GMT-3'
  },
  'America/Montevideo': {
    name: 'Montevideo',
    abbr: 'UYT',
    time: 'GMT-3'
  },
  'America/Argentina/Buenos_Aires': {
    name: 'Buenos Aires, Georgetown',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Catamarca': {
    name: 'Catamarca',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Cordoba': {
    name: 'Cordoba',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Jujuy': {
    name: 'Jujuy',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/La_Rioja': {
    name: 'La Rioja',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Mendoza': {
    name: 'Mendoza',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Rio_Gallegos': {
    name: 'Rio Gallegos',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Salta': {
    name: 'Salta',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/San_Juan': {
    name: 'San Juan',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/San_Luis': {
    name: 'San Luis',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Tucuman': {
    name: 'Tucuman',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Argentina/Ushuaia': {
    name: 'Ushuaia',
    abbr: 'ART',
    time: 'GMT-3'
  },
  'America/Godthab': { name: 'Greenland', abbr: 'WGT', time: 'GMT-3' },
  'Atlantic/Azores': { name: 'Azores', abbr: 'AZOT', time: 'GMT-1' },
  'Atlantic/Cape_Verde': { name: 'Cape Verde Islands', abbr: 'CVT', time: 'GMT-1' },
  'Atlantic/South_Georgia': { name: 'South Georgia', abbr: 'GST', time: 'GMT-2' },
  'Atlantic/Bermuda': { name: 'Bermuda', abbr: 'AST', time: 'GMT-4' },
  'Atlantic/St_Helena': { name: 'St Helena', abbr: 'GMT', time: 'GMT+0' },
  'Atlantic/Stanley': { name: 'Stanley', abbr: 'FKT', time: 'GMT-3' },
  GMT: { name: 'UTC', abbr: 'GMT', time: 'GMT+0' },
  'Europe/London': { name: 'Edinburgh, London', abbr: 'BST', time: 'GMT+1' },
  'Europe/Dublin': { name: 'Dublin', abbr: 'IST', time: 'GMT+1' },
  'Europe/Lisbon': { name: 'Lisbon', abbr: 'WEST', time: 'GMT+1' },
  'Africa/Abidjan': { name: 'Abidjan', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Accra': { name: 'Accra', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Bamako': { name: 'Bamako', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Banjul': { name: 'Banjul', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Bissau': { name: 'Bissau', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Conakry': { name: 'Conakry', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Dakar': { name: 'Dakar', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Freetown': { name: 'Freetown', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Lome': { name: 'Lome', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Monrovia': { name: 'Monrovia', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Nouakchott': { name: 'Nouakchott', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Ouagadougou': { name: 'Ouagadougou', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Sao_Tome': { name: 'Sao Tome', abbr: 'GMT', time: 'GMT+0' },
  'Africa/Timbuktu': { name: 'Timbuktu', abbr: 'GMT', time: 'GMT+0' },
  'Atlantic/Canary': { name: 'Canary Islands', abbr: 'WEST', time: 'GMT+1' },
  'Europe/Belgrade': {
    name: 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
    abbr: 'CEST',
    time: 'GMT+2'
  },
  'Europe/Sarajevo': { name: 'Sarajevo, Skopje, Warsaw, Zagreb', abbr: 'CEST', time: 'GMT+2' },
  'Europe/Brussels': { name: 'Brussels, Copenhagen, Madrid, Paris', abbr: 'CEST', time: 'GMT+2' },
  'Europe/Amsterdam': {
    name: 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
    abbr: 'CEST',
    time: 'GMT+2'
  },
  'Europe/Busingen': { name: 'Busingen', abbr: 'CEST', time: 'GMT+2' },
  'Europe/Zurich': { name: 'Zurich', abbr: 'CEST', time: 'GMT+2' },
  'Europe/Luxembourg': { name: 'Luxembourg', abbr: 'CEST', time: 'GMT+2' },
  'Europe/Monaco': { name: 'Monaco', abbr: 'CEST', time: 'GMT+2' },
  'Europe/Vaduz': { name: 'Vaduz', abbr: 'CEST', time: 'GMT+2' },
  'Africa/Algiers': { name: 'West Central Africa', abbr: 'CET', time: 'GMT+1' },
  'Europe/Bucharest': { name: 'Bucharest', abbr: 'EEST', time: 'GMT+3' },
  'Africa/Cairo': { name: 'Cairo', abbr: 'EET', time: 'GMT+2' },
  'Europe/Helsinki': {
    name: 'Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
    abbr: 'EEST',
    time: 'GMT+3'
  },
  'Europe/Kiev': { name: 'Kyiv', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Mariehamn': { name: 'Mariehamn', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Riga': { name: 'Riga', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Sofia': { name: 'Sofia', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Tallinn': { name: 'Tallinn', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Vilnius': { name: 'Vilnius', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Athens': { name: 'Athens', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Jerusalem': { name: 'Jerusalem', abbr: 'IDT', time: 'GMT+3' },
  'Africa/Harare': { name: 'Harare, Pretoria', abbr: 'CAT', time: 'GMT+2' },
  'Europe/Moscow': {
    name: 'Istanbul, Minsk, Moscow, St. Petersburg, Volgograd',
    abbr: 'MSK',
    time: 'GMT+3'
  },
  'Europe/Kaliningrad': { name: 'Kaliningrad', abbr: 'EET', time: 'GMT+2' },
  'Europe/Simferopol': { name: 'Simferopol', abbr: 'MSK', time: 'GMT+3' },
  'Europe/Uzhgorod': { name: 'Uzhgorod', abbr: 'EEST', time: 'GMT+3' },
  'Europe/Zaporozhye': { name: 'Zaporozhye', abbr: 'EEST', time: 'GMT+3' },

  'Asia/Amman': { name: 'Amman', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Beirut': { name: 'Beirut', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Damascus': { name: 'Damascus', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Gaza': { name: 'Gaza', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Hebron': { name: 'Hebron', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Nicosia': { name: 'Nicosia', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Famagusta': { name: 'Famagusta', abbr: 'EEST', time: 'GMT+3' },
  'Asia/Baghdad': { name: 'Baghdad', abbr: 'AST', time: 'GMT+3' },
  'Asia/Kuwait': { name: 'Kuwait, Riyadh', abbr: 'AST', time: 'GMT+3' },
  'Asia/Bahrain': { name: 'Bahrain', abbr: 'AST', time: 'GMT+3' },
  'Asia/Qatar': { name: 'Qatar', abbr: 'AST', time: 'GMT+3' },
  'Asia/Aden': { name: 'Aden', abbr: 'AST', time: 'GMT+3' },
  'Asia/Tehran': { name: 'Tehran', abbr: 'IRST', time: 'GMT+3:30' },
  'Asia/Dubai': { name: 'Abu Dhabi, Muscat', abbr: 'GST', time: 'GMT+4' },
  'Asia/Baku': { name: 'Baku, Tbilisi, Yerevan', abbr: 'AZT', time: 'GMT+4' },
  'Asia/Yerevan': { name: 'Yerevan', abbr: 'AMT', time: 'GMT+4' },
  'Asia/Tbilisi': { name: 'Tbilisi', abbr: 'GET', time: 'GMT+4' },
  'Asia/Kabul': { name: 'Kabul', abbr: 'AFT', time: 'GMT+4:30' },
  'Asia/Yekaterinburg': { name: 'Ekaterinburg', abbr: 'YEKT', time: 'GMT+5' },
  'Asia/Karachi': { name: 'Islamabad, Karachi, Tashkent', abbr: 'PKT', time: 'GMT+5' },
  'Asia/Oral': { name: 'Oral', abbr: 'ORAT', time: 'GMT+5' },
  'Asia/Aqtobe': { name: 'Aqtobe', abbr: 'AQTT', time: 'GMT+5' },
  'Asia/Aqtau': { name: 'Aqtau', abbr: 'AQTT', time: 'GMT+5' },
  'Asia/Atyrau': { name: 'Atyrau', abbr: 'AQTT', time: 'GMT+5' },
  'Asia/Ashgabat': { name: 'Ashgabat', abbr: 'TMT', time: 'GMT+5' },
  'Asia/Dushanbe': { name: 'Dushanbe', abbr: 'TJT', time: 'GMT+5' },
  'Asia/Tashkent': { name: 'Tashkent', abbr: 'UZT', time: 'GMT+5' },
  'Asia/Samarkand': { name: 'Samarkand', abbr: 'UZT', time: 'GMT+5' },
  'Asia/Qyzylorda': { name: 'Qyzylorda', abbr: 'QYZT', time: 'GMT+5' },
  'Asia/Kolkata': { name: 'Chennai, Kolkata, Mumbai, New Delhi', abbr: 'IST', time: 'GMT+5:30' },
  'Asia/Kathmandu': { name: 'Kathmandu', abbr: 'NPT', time: 'GMT+5:45' },
  'Asia/Colombo': { name: 'Sri Jayawardenepura', abbr: 'IST', time: 'GMT+5:30' },
  'Asia/Dhaka': { name: 'Astana, Dhaka', abbr: 'BST', time: 'GMT+6' },
  'Asia/Thimphu': { name: 'Thimphu', abbr: 'BST', time: 'GMT+6' },
  'Asia/Almaty': { name: 'Almaty, Novosibirsk', abbr: 'ALMT', time: 'GMT+6' },
  'Asia/Qostanay': { name: 'Qostanay', abbr: 'QYZT', time: 'GMT+6' },
  'Asia/Bishkek': { name: 'Bishkek', abbr: 'KGT', time: 'GMT+6' },
  'Asia/Rangoon': { name: 'Yangon Rangoon', abbr: 'MMT', time: 'GMT+6:30' },
  'Asia/Jakarta': { name: 'Jakarta', abbr: 'WIB', time: 'GMT+7' },
  'Asia/Pontianak': { name: 'Pontianak', abbr: 'WIB', time: 'GMT+7' },
  'Asia/Bangkok': { name: 'Bangkok, Hanoi, Jakarta', abbr: 'ICT', time: 'GMT+7' },
  'Asia/Hovd': { name: 'Hovd', abbr: 'HOVT', time: 'GMT+7' },
  'Asia/Phnom_Penh': { name: 'Phnom Penh', abbr: 'ICT', time: 'GMT+7' },
  'Asia/Vientiane': { name: 'Vientiane', abbr: 'ICT', time: 'GMT+7' },
  'Asia/Saigon': { name: 'Ho Chi Minh City', abbr: 'ICT', time: 'GMT+7' },
  'Asia/Barnaul': { name: 'Barnaul', abbr: 'MSK', time: 'GMT+7' },
  'Asia/Krasnoyarsk': { name: 'Krasnoyarsk', abbr: 'KRAT', time: 'GMT+7' },
  'Asia/Shanghai': {
    name: 'Beijing, Chongqing, Hong Kong SAR, Urumqi',
    abbr: 'CST',
    time: 'GMT+8'
  },
  'Asia/Chita': { name: 'Chita', abbr: 'IRKT', time: 'GMT+8' },
  'Asia/Hong_Kong': { name: 'Hong Kong', abbr: 'HKT', time: 'GMT+8' },
  'Asia/Macau': { name: 'Macau', abbr: 'CST', time: 'GMT+8' },
  'Asia/Kuala_Lumpur': { name: 'Kuala Lumpur, Singapore', abbr: 'MYT', time: 'GMT+8' },
  'Asia/Singapore': { name: 'Singapore', abbr: 'SGT', time: 'GMT+8' },
  'Asia/Taipei': { name: 'Taipei', abbr: 'CST', time: 'GMT+8' },
  'Australia/Perth': { name: 'Perth', abbr: 'AWST', time: 'GMT+8' },
  'Asia/Irkutsk': { name: 'Irkutsk, Ulaanbaatar', abbr: 'IRKT', time: 'GMT+8' },
  'Asia/Seoul': { name: 'Seoul', abbr: 'KST', time: 'GMT+9' },
  'Asia/Tokyo': { name: 'Osaka, Sapporo, Tokyo', abbr: 'JST', time: 'GMT+9' },
  'Asia/Jayapura': { name: 'Jayapura', abbr: 'WIT', time: 'GMT+9' },
  'Asia/Ulaanbaatar': { name: 'Ulaanbaatar', abbr: 'ULAT', time: 'GMT+8' },
  'Asia/Yakutsk': { name: 'Yakutsk', abbr: 'YAKT', time: 'GMT+9' },
  'Australia/Darwin': { name: 'Darwin', abbr: 'ACST', time: 'GMT+9:30' },
  'Australia/Adelaide': { name: 'Adelaide', abbr: 'ACST', time: 'GMT+9:30' },
  'Australia/Broken_Hill': { name: 'Broken Hill', abbr: 'ACST', time: 'GMT+9:30' },
  'Australia/Sydney': { name: 'Canberra, Melbourne, Sydney', abbr: 'AEDT', time: 'GMT+11' },
  'Australia/Brisbane': { name: 'Brisbane', abbr: 'AEST', time: 'GMT+10' },
  'Australia/Lindeman': { name: 'Lindeman', abbr: 'AEST', time: 'GMT+10' },
  'Australia/Hobart': { name: 'Hobart', abbr: 'AEDT', time: 'GMT+11' },
  'Australia/Currie': { name: 'Currie', abbr: 'AEDT', time: 'GMT+11' },
  'Australia/Lord_Howe': { name: 'Lord Howe Island', abbr: 'LHST', time: 'GMT+10:30' },
  'Asia/Vladivostok': { name: 'Vladivostok', abbr: 'VLAT', time: 'GMT+10' },
  'Asia/Sakhalin': { name: 'Sakhalin', abbr: 'SAKT', time: 'GMT+11' },
  'Pacific/Guam': { name: 'Guam, Port Moresby', abbr: 'ChST', time: 'GMT+10' },
  'Pacific/Port_Moresby': { name: 'Port Moresby', abbr: 'PGT', time: 'GMT+10' },
  'Asia/Magadan': { name: 'Magadan, Solomon Islands, New Caledonia', abbr: 'MAGT', time: 'GMT+11' },
  'Pacific/Bougainville': { name: 'Bougainville', abbr: 'BST', time: 'GMT+11' },
  'Pacific/Efate': { name: 'Efate', abbr: 'VUT', time: 'GMT+11' },
  'Pacific/Guadalcanal': { name: 'Guadalcanal', abbr: 'SBT', time: 'GMT+11' },
  'Pacific/Kosrae': { name: 'Kosrae', abbr: 'KOST', time: 'GMT+11' },
  'Pacific/Norfolk': { name: 'Norfolk', abbr: 'NFT', time: 'GMT+11' },
  'Pacific/Noumea': { name: 'Noumea', abbr: 'NCT', time: 'GMT+11' },
  'Pacific/Pohnpei': { name: 'Pohnpei', abbr: 'PONT', time: 'GMT+11' },
  'Asia/Kamchatka': { name: 'Kamchatka, Marshall Islands', abbr: 'PETT', time: 'GMT+12' },
  'Pacific/Funafuti': { name: 'Funafuti', abbr: 'TVT', time: 'GMT+12' },
  'Pacific/Kwajalein': { name: 'Kwajalein', abbr: 'KWT', time: 'GMT+12' },
  'Pacific/Majuro': { name: 'Majuro', abbr: 'MHT', time: 'GMT+12' },
  'Pacific/Nauru': { name: 'Nauru', abbr: 'NRT', time: 'GMT+12' },
  'Pacific/Tarawa': { name: 'Tarawa', abbr: 'GILT', time: 'GMT+12' },
  'Pacific/Wake': { name: 'Wake', abbr: 'WAKT', time: 'GMT+12' },
  'Pacific/Wallis': { name: 'Wallis', abbr: 'WFT', time: 'GMT+12' },
  'Pacific/Fiji': { name: 'Fiji Islands', abbr: 'FJT', time: 'GMT+12' },
  'Pacific/Apia': { name: 'Apia', abbr: 'WSST', time: 'GMT+13' },
  'Pacific/Auckland': { name: 'Auckland, Wellington', abbr: 'NZDT', time: 'GMT+13' },
  'Pacific/Chatham': { name: 'Chatham Islands', abbr: 'CHAST', time: 'GMT+13:45' },
  'Pacific/Tongatapu': { name: "Nuku'alofa", abbr: 'TOT', time: 'GMT+13' },
  'Pacific/Enderbury': { name: 'Enderbury', abbr: 'PHOT', time: 'GMT+13' },
  'Pacific/Fakaofo': { name: 'Fakaofo', abbr: 'TKT', time: 'GMT+13' },
  'Pacific/Tokelau': { name: 'Tokelau', abbr: 'TKT', time: 'GMT+13' },
  'Pacific/Kiritimati': { name: 'Kiritimati', abbr: 'LINT', time: 'GMT+14' },
  'Europe/Berlin': { name: 'Frankfurt', abbr: 'CET', time: 'GMT+1' }
};

export const TIMEZONES = {
  'Pacific/Midway': 'Midway Island, Samoa',
  'Pacific/Niue': 'Niue',
  'Pacific/Pago_Pago': 'Pago Pago',
  'Pacific/Honolulu': 'Hawaii',
  'Pacific/Rarotonga': 'Rarotonga',
  'Pacific/Tahiti': 'Tahiti',
  'Pacific/Marquesas': 'Marquesas Islands',
  'America/Adak': 'Adak',
  'America/Anchorage': 'Anchorage',
  'America/Juneau': 'Alaska',
  'America/Nome': 'Nome',
  'America/Sitka': 'Sitka',
  'America/Yakutat': 'Yakutat',
  'America/Dawson': 'Dawson, Yukon',
  'America/Whitehorse': 'Whitehorse',
  'America/Creston': 'Creston',
  'America/Dawson_Creek': 'Dawson Creek',
  'America/Fort_Nelson': 'Fort Nelson',
  'America/Los_Angeles': 'Pacific Time',
  'America/Tijuana': 'Tijuana',
  'America/Vancouver': 'Vancouver',
  'America/Phoenix': 'Arizona',
  'America/Boise': 'Mountain Time',
  'America/Cambridge_Bay': 'Cambridge Bay',
  'America/Denver': 'Denver',
  'America/Edmonton': 'Edmonton',
  'America/Hermosillo': 'Hermosillo',
  'America/Inuvik': 'Inuvik',
  'America/Mazatlan': 'Mazatlan',
  'America/Ojinaga': 'Ojinaga',
  'America/Yellowknife': 'Yellowknife',
  'America/Chihuahua': 'Chihuahua, La Paz, Mazatlan',
  'America/Belize': 'Central America',
  'America/Chicago': 'Central Time',
  'America/Matamoros': 'Matamoros',
  'America/Regina': 'Saskatchewan',
  'America/Mexico_City': 'Guadalajara, Mexico City, Monterrey',
  'America/Bahia_Banderas': 'Bahia Banderas',
  'America/Merida': 'Merida',
  'America/Monterrey': 'Monterrey',
  'America/Winnipeg': 'Winnipeg',
  'America/Detroit': 'Eastern Time',
  'America/New_York': 'New York',
  'America/Bogota': 'Bogota, Lima, Quito',
  'America/Caracas': 'Caracas, La Paz',
  'America/Anguilla': 'Anguilla',
  'America/Antigua': 'Antigua',
  'America/Aruba': 'Aruba',
  'America/Asuncion': 'Asuncion',
  'America/Barbados': 'Barbados',
  'America/Blanc-Sablon': 'Blanc-Sablon',
  'America/Boa_Vista': 'Boa Vista',
  'America/Campo_Grande': 'Campo Grande',
  'America/Cuiaba': 'Cuiaba',
  'America/Curacao': 'Curacao',
  'America/Dominica': 'Dominica',
  'America/Eirunepe': 'Eirunepe',
  'America/Glace_Bay': 'Glace Bay',
  'America/Goose_Bay': 'Goose Bay',
  'America/Grenada': 'Grenada',
  'America/Guadeloupe': 'Guadeloupe',
  'America/Guyana': 'Guyana',
  'America/Havana': 'Havana',
  'America/La_Paz': 'La Paz',
  'America/Manaus': 'Manaus',
  'America/Marigot': 'Marigot',
  'America/Martinique': 'Martinique',
  'America/Montserrat': 'Montserrat',
  'America/Nassau': 'Nassau',
  'America/Paramaribo': 'Paramaribo',
  'America/Port_of_Spain': 'Port of Spain',
  'America/Port-au-Prince': 'Port-au-Prince',
  'America/Porto_Velho': 'Porto Velho',
  'America/Puerto_Rico': 'Puerto Rico',
  'America/Santo_Domingo': 'Santo Domingo',
  'America/St_Barthelemy': 'St Barthelemy',
  'America/St_Kitts': 'St Kitts',
  'America/St_Lucia': 'St Lucia',
  'America/St_Thomas': 'St Thomas',
  'America/St_Vincent': 'St Vincent',
  'America/Thule': 'Thule',
  'America/Tortola': 'Tortola',
  'America/Toronto': 'Toronto',
  'America/Lower_Princes': 'Lower Princes',
  'America/Santiago': 'Santiago',
  'America/St_Johns': 'Newfoundland and Labrador',
  'America/Sao_Paulo': 'Brasilia',
  'America/Montevideo': 'Montevideo',
  'America/Argentina/Buenos_Aires': 'Buenos Aires, Georgetown',
  'America/Argentina/Catamarca': 'Catamarca',
  'America/Argentina/Cordoba': 'Cordoba',
  'America/Argentina/Jujuy': 'Jujuy',
  'America/Argentina/La_Rioja': 'La Rioja',
  'America/Argentina/Mendoza': 'Mendoza',
  'America/Argentina/Rio_Gallegos': 'Rio Gallegos',
  'America/Argentina/Salta': 'Salta',
  'America/Argentina/San_Juan': 'San Juan',
  'America/Argentina/San_Luis': 'San Luis',
  'America/Argentina/Tucuman': 'Tucuman',
  'America/Argentina/Ushuaia': 'Ushuaia',
  'America/Godthab': 'Greenland',
  'Atlantic/Azores': 'Azores',
  'Atlantic/Cape_Verde': 'Cape Verde Islands',
  'Atlantic/South_Georgia': 'South Georgia',
  'Atlantic/Bermuda': 'Bermuda',
  'Atlantic/St_Helena': 'St Helena',
  'Atlantic/Stanley': 'Stanley',
  GMT: 'UTC',
  'Europe/London': 'Edinburgh, London',
  'Europe/Dublin': 'Dublin',
  'Europe/Lisbon': 'Lisbon',
  'Africa/Abidjan': 'Abidjan',
  'Africa/Accra': 'Accra',
  'Africa/Bamako': 'Bamako',
  'Africa/Banjul': 'Banjul',
  'Africa/Bissau': 'Bissau',
  'Africa/Conakry': 'Conakry',
  'Africa/Dakar': 'Dakar',
  'Africa/Freetown': 'Freetown',
  'Africa/Lome': 'Lome',
  'Africa/Monrovia': 'Monrovia',
  'Africa/Nouakchott': 'Nouakchott',
  'Africa/Ouagadougou': 'Ouagadougou',
  'Africa/Sao_Tome': 'Sao Tome',
  'Africa/Timbuktu': 'Timbuktu',
  'Atlantic/Canary': 'Canary Islands',
  'Europe/Belgrade': 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
  'Europe/Sarajevo': 'Sarajevo, Skopje, Warsaw, Zagreb',
  'Europe/Brussels': 'Brussels, Copenhagen, Madrid, Paris',
  'Europe/Amsterdam': 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
  'Europe/Busingen': 'Busingen',
  'Europe/Zurich': 'Zurich',
  'Europe/Luxembourg': 'Luxembourg',
  'Europe/Monaco': 'Monaco',
  'Europe/Vaduz': 'Vaduz',
  'Africa/Algiers': 'West Central Africa',
  'Europe/Bucharest': 'Bucharest',
  'Africa/Cairo': 'Cairo',
  'Europe/Helsinki': 'Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
  'Europe/Kiev': 'Kyiv',
  'Europe/Mariehamn': 'Mariehamn',
  'Europe/Riga': 'Riga',
  'Europe/Sofia': 'Sofia',
  'Europe/Tallinn': 'Tallinn',
  'Europe/Vilnius': 'Vilnius',
  'Europe/Athens': 'Athens',
  'Asia/Jerusalem': 'Jerusalem',
  'Africa/Harare': 'Harare, Pretoria',
  'Europe/Moscow': 'Istanbul, Minsk, Moscow, St. Petersburg, Volgograd',
  'Europe/Kaliningrad': 'Kaliningrad',
  'Europe/Simferopol': 'Simferopol',
  'Europe/Uzhgorod': 'Uzhgorod',
  'Europe/Zaporozhye': 'Zaporozhye',
  'Asia/Amman': 'Amman',
  'Asia/Beirut': 'Beirut',
  'Asia/Damascus': 'Damascus',
  'Asia/Gaza': 'Gaza',
  'Asia/Hebron': 'Hebron',
  'Asia/Nicosia': 'Nicosia',
  'Asia/Famagusta': 'Famagusta',
  'Asia/Baghdad': 'Baghdad',
  'Asia/Kuwait': 'Kuwait, Riyadh',
  'Asia/Bahrain': 'Bahrain',
  'Asia/Qatar': 'Qatar',
  'Asia/Aden': 'Aden',
  'Asia/Tehran': 'Tehran',
  'Asia/Dubai': 'Abu Dhabi, Muscat',
  'Asia/Baku': 'Baku, Tbilisi, Yerevan',
  'Asia/Yerevan': 'Yerevan',
  'Asia/Tbilisi': 'Tbilisi',
  'Asia/Kabul': 'Kabul',
  'Asia/Yekaterinburg': 'Ekaterinburg',
  'Asia/Karachi': 'Islamabad, Karachi, Tashkent',
  'Asia/Oral': 'Oral',
  'Asia/Aqtobe': 'Aqtobe',
  'Asia/Aqtau': 'Aqtau',
  'Asia/Atyrau': 'Atyrau',
  'Asia/Ashgabat': 'Ashgabat',
  'Asia/Dushanbe': 'Dushanbe',
  'Asia/Tashkent': 'Tashkent',
  'Asia/Samarkand': 'Samarkand',
  'Asia/Qyzylorda': 'Qyzylorda',
  'Asia/Kolkata': 'Chennai, Kolkata, Mumbai, New Delhi',
  'Asia/Kathmandu': 'Kathmandu',
  'Asia/Colombo': 'Sri Jayawardenepura',
  'Asia/Dhaka': 'Astana, Dhaka',
  'Asia/Thimphu': 'Thimphu',
  'Asia/Almaty': 'Almaty, Novosibirsk',
  'Asia/Qostanay': 'Qostanay',
  'Asia/Bishkek': 'Bishkek',
  'Asia/Rangoon': 'Yangon Rangoon',
  'Asia/Jakarta': 'Jakarta',
  'Asia/Pontianak': 'Pontianak',
  'Asia/Bangkok': 'Bangkok, Hanoi, Jakarta',
  'Asia/Hovd': 'Hovd',
  'Asia/Phnom_Penh': 'Phnom Penh',
  'Asia/Vientiane': 'Vientiane',
  'Asia/Saigon': 'Ho Chi Minh City',
  'Asia/Barnaul': 'Barnaul',
  'Asia/Krasnoyarsk': 'Krasnoyarsk',
  'Asia/Shanghai': 'Beijing, Chongqing, Hong Kong SAR, Urumqi',
  'Asia/Chita': 'Chita',
  'Asia/Hong_Kong': 'Hong Kong',
  'Asia/Macau': 'Macau',
  'Asia/Kuala_Lumpur': 'Kuala Lumpur, Singapore',
  'Asia/Singapore': 'Singapore',
  'Asia/Taipei': 'Taipei',
  'Australia/Perth': 'Perth',
  'Asia/Irkutsk': 'Irkutsk, Ulaanbaatar',
  'Asia/Seoul': 'Seoul',
  'Asia/Tokyo': 'Osaka, Sapporo, Tokyo',
  'Asia/Jayapura': 'Jayapura',
  'Asia/Ulaanbaatar': 'Ulaanbaatar',
  'Asia/Yakutsk': 'Yakutsk',
  'Australia/Darwin': 'Darwin',
  'Australia/Adelaide': 'Adelaide',
  'Australia/Broken_Hill': 'Broken Hill',
  'Australia/Sydney': 'Canberra, Melbourne, Sydney',
  'Australia/Brisbane': 'Brisbane',
  'Australia/Lindeman': 'Lindeman',
  'Australia/Hobart': 'Hobart',
  'Australia/Currie': 'Currie',
  'Australia/Lord_Howe': 'Lord Howe Island',
  'Asia/Vladivostok': 'Vladivostok',
  'Asia/Sakhalin': 'Sakhalin',
  'Pacific/Guam': 'Guam, Port Moresby',
  'Pacific/Port_Moresby': 'Port Moresby',
  'Asia/Magadan': 'Magadan, Solomon Islands, New Caledonia',
  'Pacific/Bougainville': 'Bougainville',
  'Pacific/Efate': 'Efate',
  'Pacific/Guadalcanal': 'Guadalcanal',
  'Pacific/Kosrae': 'Kosrae',
  'Pacific/Norfolk': 'Norfolk',
  'Pacific/Noumea': 'Noumea',
  'Pacific/Pohnpei': 'Pohnpei',
  'Asia/Kamchatka': 'Kamchatka, Marshall Islands',
  'Pacific/Funafuti': 'Funafuti',
  'Pacific/Kwajalein': 'Kwajalein',
  'Pacific/Majuro': 'Majuro',
  'Pacific/Nauru': 'Nauru',
  'Pacific/Tarawa': 'Tarawa',
  'Pacific/Wake': 'Wake',
  'Pacific/Wallis': 'Wallis',
  'Pacific/Fiji': 'Fiji Islands',
  'Pacific/Apia': 'Apia',
  'Pacific/Auckland': 'Auckland, Wellington',
  'Pacific/Chatham': 'Chatham Islands',
  'Pacific/Tongatapu': "Nuku'alofa",
  'Pacific/Enderbury': 'Enderbury',
  'Pacific/Fakaofo': 'Fakaofo',
  'Pacific/Tokelau': 'Tokelau',
  'Pacific/Kiritimati': 'Kiritimati',
  'Europe/Berlin': 'Frankfurt'
};

export const defaultThreshold: ThresholdConfig = {
  id: 'ashrae',
  mode: 'auto',
  name: 'ASHRAE',
  created: null,
  updated: null,
  createdBy: 'auto',
  thresholds: {
    ci: {
      max: 500,
      min: 0,
      lowerWarning: 0,
      upperWarning: 50,
      lowerCritical: 0,
      upperCritical: 100
    },
    co: {
      max: 200,
      min: 0,
      lowerWarning: 0,
      upperWarning: 9,
      lowerCritical: 0,
      upperCritical: 35
    },
    o3: {
      max: 5,
      min: 0,
      lowerWarning: 0,
      upperWarning: 0.07,
      lowerCritical: 0,
      upperCritical: 0.14
    },
    aqi: {
      max: 500,
      min: 0,
      lowerWarning: 0,
      upperWarning: 50,
      lowerCritical: 0,
      upperCritical: 100
    },
    ch4: {
      max: 10000,
      min: 0,
      lowerWarning: 0,
      upperWarning: 1000,
      lowerCritical: 0,
      upperCritical: 2000
    },
    co2: {
      max: 5000,
      min: 400,
      lowerWarning: 400,
      upperWarning: 800,
      lowerCritical: 400,
      upperCritical: 1000
    },
    hum: {
      max: 100,
      min: 0,
      lowerWarning: 30,
      upperWarning: 60,
      lowerCritical: 20,
      upperCritical: 70
    },
    no2: {
      max: 1,
      min: 0,
      lowerWarning: 0,
      upperWarning: 0.053,
      lowerCritical: 0,
      upperCritical: 0.1
    },
    so2: {
      max: 1,
      min: 0,
      lowerWarning: 0,
      upperWarning: 0.075,
      lowerCritical: 0,
      upperCritical: 0.15
    },
    temp: {
      max: 80,
      min: -40,
      lowerWarning: 22,
      upperWarning: 28,
      lowerCritical: 18,
      upperCritical: 30
    },
    tvoc: {
      max: 1000,
      min: 0,
      lowerWarning: 0,
      upperWarning: 100,
      lowerCritical: 0,
      upperCritical: 200
    },
    pm25_m: {
      max: 1000,
      min: 0,
      lowerWarning: 0,
      upperWarning: 12,
      lowerCritical: 0,
      upperCritical: 35
    },
    pn03_m: {
      max: 655,
      min: 0,
      lowerWarning: 0,
      upperWarning: 10,
      lowerCritical: 0,
      upperCritical: 20
    },
    pm100_m: {
      max: 1000,
      min: 0,
      lowerWarning: 0,
      upperWarning: 35,
      lowerCritical: 0,
      upperCritical: 50
    },
    pressure: {
      max: 692,
      min: 0,
      lowerWarning: 395,
      upperWarning: 415,
      lowerCritical: 390,
      upperCritical: 420
    }
  },
  description: 'Ashrae standard 34-2019'
};

export const CUSTOM_THRESHOLD_CONFIG = {
  aqi: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 500,
    incomplete: true
  },
  ci: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 500,
    incomplete: true
  },
  pm100_m: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 1000,
    incomplete: true
  },
  pm25_m: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 1000,
    incomplete: true
  },
  co2: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 400,
    max: 5000,
    incomplete: true
  },
  tvoc: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 1000,
    incomplete: true
  },
  temp: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: -40,
    max: 80,
    incomplete: true
  },
  hum: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 100,
    incomplete: true
  },
  pressure: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 692,
    incomplete: true
  },
  pn03_m: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 655,
    incomplete: true
  },
  co: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 200,
    incomplete: true
  },
  no2: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 1,
    incomplete: true
  },
  o3: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 5,
    incomplete: true
  },
  so2: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 1,
    incomplete: true
  },
  ch4: {
    lowerCritical: 0,
    lowerWarning: 0,
    upperCritical: 0,
    upperWarning: 0,
    min: 0,
    max: 10000,
    incomplete: true
  }
};
